import React, {useCallback} from 'react';
import styles from './styles.module.scss';
import {ReactComponent as OkIcon} from 'svg/ok.svg';
import {ReactComponent as AndroidIcon} from 'svg/android-icon.svg';
import {ReactComponent as IOSIcon} from 'svg/ios-icon.svg';
import {ReactComponent as DesktopIcon} from 'svg/desktop-icon.svg';
import {IPlatforms} from 'types/offers';
import {useFilterContext} from 'providers/filter-context';
import {useOffersContext} from 'providers/offers-context';
import {Button} from '@chakra-ui/react';
import {ListFilter as Filter} from 'lucide-react';
import Text from 'components/typografy/text';
import classNames from 'classnames';
import {IoClose} from 'react-icons/io5';
import useIsMobile from '../../hooks/useIsMobile';
import {Platform} from '../../enums/devices';

const DeviceTypeFilters: React.FC = () => {
  // Context hooks for managing filter state and offers
  const {filterType, setFilterType, isFilterOpen, setIsFilterOpen} = useFilterContext();
  const {setCanFetchList, canFetchList} = useOffersContext();
  
  // Check if we're on mobile (768px breakpoint to match CSS)
  const isMobile = useIsMobile(768);

  // Memoized handler for filter changes
  const onChange = useCallback((key: keyof IPlatforms, value: boolean) => {
    if (!canFetchList) {
      setCanFetchList(true);
    }

    setFilterType((state) => ({
      ...state,
      [key]: value
    }));
  }, [canFetchList, setCanFetchList, setFilterType]);

  // Handler for clearing all filters
  const onClearClick = useCallback(() => {
    setFilterType({android: true, ios: true, desktop: true});
  }, [setFilterType]);

  // Handler for toggling filter visibility
  const toggleFilter = useCallback(() => {
    setIsFilterOpen(!isFilterOpen);
  }, [isFilterOpen, setIsFilterOpen]);

  // Count of active filters for the badge
  const activeFiltersCount = Object.values(filterType).filter(Boolean).length;

  return (
    <div className={styles.wrapper}>
      <div className={styles.filterHeader}>
        <div className={styles.title}>Offers</div>
        <div className={styles.filterButton}>
          {/* Show clear filters button only when filters are closed */}
          {!isFilterOpen && (
            <Button variant="plain" onClick={onClearClick}>
              <Text variant={'small'} nativeClassname={styles.btn}>
                Clear filters
              </Text>
            </Button>
          )}
          {/* Filter toggle button with active filter count badge */}
          <button onClick={toggleFilter} className={classNames(styles.trigger, {[styles.green]: !!activeFiltersCount})}>
            {isFilterOpen ? (
              <>
                <IoClose /> Close
              </>
            ) : (
              <>
                <Filter /> Filter
              </>
            )}{' '}
            {!!activeFiltersCount && activeFiltersCount}
          </button>
        </div>
      </div>      <div className={styles.filterWrapper}>
        <div className={classNames(styles.filterContent, {
          [styles.open]: isFilterOpen || !isMobile,
          [styles.closed]: !isFilterOpen && isMobile
        })}>
          <label className={styles.checkboxWrapper}>
            <input
              type="checkbox"
              className={styles.hidden}
              onChange={(event) => {
                onChange(Platform.ANDROID, event.target.checked);
              }}
              checked={filterType.android}
            />
            <span className={styles.checkBox}>
              <OkIcon />
            </span>
            <AndroidIcon />
            <div>Android</div>
          </label>
          <label className={styles.checkboxWrapper}>
            <input
              type="checkbox"
              className={styles.hidden}
              onChange={(event) => {
                onChange(Platform.IOS, event.target.checked);
              }}
              checked={filterType.ios}
            />
            <span className={styles.checkBox}>
              <OkIcon />
            </span>
            <IOSIcon />
            <div>iOS</div>
          </label>
          <label className={styles.checkboxWrapper}>
            <input
              type="checkbox"
              className={styles.hidden}
              onChange={(event) => {
                onChange(Platform.DESKTOP, event.target.checked);
              }}
              checked={filterType.desktop}
            />
            <span className={styles.checkBox}>
              <OkIcon />
            </span>
            <DesktopIcon />
            <div>Desktop</div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default React.memo(DeviceTypeFilters);
